import Script from 'next/script'
import { useEffect, useEffectEvent } from 'react'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { useIsShoppingAgentEnabled } from '@/features/shop/services/ShoppingAgent/hooks'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { logError } from '@/features/shared/utils/logger'
import { fetchGECXToken } from '@/features/shop/services/ShoppingAgent/tokenService'
import {
  type AddToCartEventDetail,
  type CartUpdateResult,
} from '@/features/shop/services/ShoppingAgent/types'
import { GECX_SDK_URL, GECX_CUSTOMER_ID } from '@/features/shop/services/ShoppingAgent/constants'
import { updateCart } from '@/features/shop/services/Cart/fetchers'
import { useCartGraphQLInput } from '@/features/shop/services/Cart/useCartGraphQLInput'
import { cartQueryOptions } from '@/features/shop/services/Cart/queries'
import { type Product } from '@/features/shop/services/Product/types'
import { gecxProvider } from '@/features/shop/services/ShoppingAgent/gecxProvider'
import { getErrorMessage } from '@/features/shared/utils/dataFetching/utils'
import { invalidateCartQueries } from '@/features/shop/services/Cart/mutations'

const isShoppableCartLine = (line: Product) => !line.isCustomProduct

export function ShoppingAgentScript() {
  const storeParams = useStoreParams()
  const state = useGlobalDefaultShoppingAddress().state ?? ''
  const cartInput = useCartGraphQLInput()

  const gecxEnabled = useIsShoppingAgentEnabled()

  const tokenFunc = useEffectEvent((sessionId?: string) =>
    fetchGECXToken({
      sessionId,
      storeParams,
    }),
  )
  const handleCartUpdate = useEffectEvent(
    async (
      products: { productId: string; quantity: number }[],
      action: 'add' | 'set',
    ): Promise<CartUpdateResult> => {
      const queryClient = getQueryClient()
      const queryKey = cartQueryOptions({ storeParams, state }).queryKey
      const cart = queryClient.getQueryData(queryKey)

      try {
        // Keyed by product id so each cart line gets exactly one request,
        // which is what makes the parallel updates below safe.
        const plan = new Map<
          number,
          { id: number; targetQty: number; note: string; description: string }
        >()

        for (const { productId, quantity } of products) {
          const id = Number(productId)
          if (!Number.isFinite(id)) {
            throw new Error(`Invalid productId: ${productId}`)
          }
          const existing = cart?.shopping_cart_lines
            .filter(isShoppableCartLine)
            .find((line) => line.id === id)
          const baseQty = plan.get(id)?.targetQty ?? existing?.quantity ?? 0

          plan.set(id, {
            id,
            targetQty: action === 'add' ? baseQty + quantity : quantity,
            note: existing?.note ?? '',
            description: existing?.description ?? '',
          })
        }

        // The agent only gives us product ids, so every line is applied by id.
        // A quantity update is an upsert: it creates the line when absent.
        const failures: string[] = []

        await Promise.all(
          [...plan.values()].map(async ({ id, targetQty, note, description }) => {
            try {
              await updateCart(cartInput, {
                productId: String(id),
                description,
                quantity: targetQty,
                notes: note,
              })
            } catch (error) {
              failures.push(
                `gecx shoppingAgent handleCartUpdate product ${id}: ${getErrorMessage(error)}`,
              )
            }
          }),
        )

        await invalidateCartQueries({ queryClient, queryKey })

        if (failures.length > 0) {
          return { success: false, error: failures.join('; ') }
        }
        return { success: true }
      } catch (error) {
        return {
          success: false,
          error: getErrorMessage(error),
        }
      }
    },
  )

  const handleGetCart = useEffectEvent((): AddToCartEventDetail[] => {
    const queryClient = getQueryClient()
    const queryKey = cartQueryOptions({ storeParams, state }).queryKey
    const cart = queryClient.getQueryData(queryKey)

    if (!cart) return []
    return cart.shopping_cart_lines.filter(isShoppableCartLine).map((line) => ({
      productId: line.id.toString(),
      quantity: line.quantity,
    }))
  })

  useEffect(() => {
    if (!gecxEnabled) return
    gecxProvider.init({
      customerId: GECX_CUSTOMER_ID,
      tokenFunc,
      handleCartUpdate,
      handleGetCart,
    })
  }, [gecxEnabled])

  useEffect(() => {
    if (!gecxEnabled) return
    gecxProvider.syncStore(storeParams)
  }, [gecxEnabled, storeParams])

  if (!gecxEnabled) return null

  return (
    <Script
      id="gecx-shopping-agent"
      src={GECX_SDK_URL}
      strategy="lazyOnload"
      onError={(err) => logError('[ShoppingAgent] SDK failed to load', { err })}
    />
  )
}
